
import { DISCOUNT_CATEGORY } from '~~/types/discounts';
import type { MappedDiscount, VolumeDiscount } from '~~/types/discounts';

const findVolumeDiscount = ({
  currentQuantity,
  volumeDiscounts,
  findCurrent = false,
}: {
  currentQuantity: number;
  volumeDiscounts: VolumeDiscount[];
  findCurrent?: boolean;
}): VolumeDiscount | null => {

  if (!volumeDiscounts?.length) {

    return null;

  }

  const sortedDiscounts = [...volumeDiscounts].sort((a, b) => a.volume - b.volume);

  // Find index of current applicable discount
  let currentIndex = -1;
  for (let i = sortedDiscounts.length - 1; i >= 0; i--) {

    const discount = sortedDiscounts[i];

    if (discount && currentQuantity >= discount.volume) {

      currentIndex = i;
      break;
    
    }
  
  }

  if (findCurrent) {

    return currentIndex >= 0 ? sortedDiscounts[currentIndex] ?? null : null;
  
  } else {

    return currentIndex + 1 < sortedDiscounts.length ? sortedDiscounts[currentIndex + 1] ?? null : null;
  
  }

};

export const useInactiveVolumeDiscounts = () => {

  const { lineItems } = useLineItems();
  const { isDiscountConflict } = useVolumeDiscountsStore();

  const inactiveVolumeDiscounts = computed<MappedDiscount[]>(() => {

    if (!lineItems.value?.length) {

      return [];
    
    }

    const discountMap = new Map<string, MappedDiscount>();

    lineItems.value.forEach((item) => {

      const volumeDiscounts = item.volumeDiscountsForGroup || [];
      const targetDiscount = findVolumeDiscount({
        currentQuantity: item.qty,
        volumeDiscounts,
        findCurrent: isDiscountConflict.value,
      });

      if (targetDiscount) {

        const discountKey = `${targetDiscount.discountPercentage}-${targetDiscount.volume}`;

        if (discountMap.has(discountKey)) {

          const existingDiscount = discountMap.get(discountKey);

          if (existingDiscount) {

            existingDiscount.products.push({
              name: item.title || '',
              discountedAmount: 0,
            });
          
          }
        
        } else {

          discountMap.set(discountKey, {
            id: `inactive-volume-${discountKey}`,
            name: 'Volume Discount',
            type: 'relative' as const,
            target: 'lineItems' as const,
            value: targetDiscount.discountPercentage,
            discountedAmount: 0,
            requiresDiscountCode: false,
            products: [{ name: item.title || '', discountedAmount: 0 }],
            category: DISCOUNT_CATEGORY.VOLUME,
            minimalQuantity: targetDiscount.volume,
          });
        
        }
      
      }
    
    });

    return Array.from(discountMap.values());
  
  });

  return {
    inactiveVolumeDiscounts,
  };

};
